import { ChevronDown } from '@untitled-ui/icons-react/build/cjs';
import React, { useState } from 'react';
import TooltipCmp from 'src/components/TooltipCmp';

interface IProps {
    /** Card content shown below the cell on click. */
    message: React.ReactNode;
    children: React.ReactNode;
    parentClassName?: string;
    wrapperClassName?: string;
    /** Only the chevron opens the card (the cell text keeps its own click, e.g. row navigation). */
    isChevronOnlyTrigger?: boolean;
    /**
     * Skip the <td>-sized chrome (hover/active background + side padding). Inside a compact wrapper —
     * e.g. nested next to another icon in the same cell — that chrome reads as a stray highlight box.
     */
    isCompactTrigger?: boolean;
}

/**
 * CellDropdown — a table cell that opens a V3 card on click: the whole cell highlights and shows a
 * chevron, the card drops below it. Port of WALHQ's `OrderDetailsContent/CellDropdown` so portal
 * tables read the same as WALHQ's (Route Overview origin / destination, …).
 */
const CellDropdown = ({ message, children, parentClassName = '', wrapperClassName = 'flex items-center gap-x-1 h-full', isChevronOnlyTrigger = false, isCompactTrigger = false }: IProps) => {
    const [isOpen, setIsOpen] = useState(false);

    const chevron = (
        <div className={`table-icon-wrap flex-none cursor-pointer flex items-center h-full px-1 ${isOpen ? '!opacity-100' : ''}`}>
            <ChevronDown className="w-4 h-4 text-grayLight600 flex-none" />
        </div>
    );

    const withTooltip = (trigger: React.ReactNode) => (
        <TooltipCmp
            TooltipCustomClass="tooltipV3-content-wrap"
            isArrow={false}
            position="bottom"
            openOnClick
            isTooltipInteractive
            parentClassName={parentClassName}
            onOpenChange={setIsOpen}
            message={message}
        >
            {trigger}
        </TooltipCmp>
    );

    const tableCellChrome = isCompactTrigger ? '' : 'table-cell-clickable table-col-tooltip-space-common-xs';

    if (isChevronOnlyTrigger) {
        return (
            <div className={`${wrapperClassName} ${tableCellChrome} ${isOpen && !isCompactTrigger ? '!bg-utilityGray200' : ''}`}>
                {children}
                <div onClick={(e) => e.stopPropagation()} className="h-full">
                    {withTooltip(chevron)}
                </div>
            </div>
        );
    }

    return withTooltip(
        <div className={`${wrapperClassName} ${tableCellChrome} cursor-pointer ${isOpen && !isCompactTrigger ? '!bg-utilityGray200' : ''}`}>
            {children}
            {chevron}
        </div>
    );
};

export default CellDropdown;
